import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

/**
 * Confirmation step before a write. Callers should name the action and its impact
 * (what changes, how many rows, which environment) in `title` / `message`.
 */
export function ConfirmPopup({
	action,
	open,
	setOpen,
	title = 'We need your confirmation.',
	message = 'Please confirm that you really want to perform this action.',
	confirmText = 'Confirm',
	destructive = false,
}) {
	const [pending, setPending] = useState(false);
	const pendingRef = useRef(false);

	// A promise-returning action keeps both buttons disabled until it settles (no double submit);
	// a sync action closes straight away as before. Failures close the dialog and are logged,
	// callers show their own error toast.
	const handleAction = () => {
		if (pendingRef.current) return;
		let result;
		try {
			result = action();
		} catch (err) {
			setOpen(false);
			throw err;
		}
		if (typeof result?.then !== 'function') {
			setOpen(false);
			return;
		}
		pendingRef.current = true;
		setPending(true);
		Promise.resolve(result)
			.catch((err) => console.error(err))
			.finally(() => {
				pendingRef.current = false;
				setPending(false);
				setOpen(false);
			});
	};

	const handleClose = () => {
		if (!pending) setOpen(false);
	};

	return (
		<Dialog open={open} onClose={handleClose}>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				{typeof message === 'string' ? <DialogContentText>{message}</DialogContentText> : message}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={pending}>
					Cancel
				</Button>
				<Button
					onClick={handleAction}
					loading={pending}
					disabled={pending}
					color={destructive ? 'error' : 'primary'}
					variant={destructive ? 'contained' : 'text'}
				>
					{confirmText}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
ConfirmPopup.propTypes = {
	action: PropTypes.func,
	open: PropTypes.bool,
	setOpen: PropTypes.func,
	title: PropTypes.node,
	message: PropTypes.node,
	confirmText: PropTypes.node,
	destructive: PropTypes.bool,
};
